import { memo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, IconButton } from "@mui/material";
import {
  OperationDTO,
  OperationsApiService,
} from "@zenith/services/transactions";
import {
  PAYMENTS_RECEIPT_DOWNLOAD_FILENAME,
} from "@zenith/data/constants";
import { formatCurrency } from "@zenith/utils";
import { EmailIcon } from "@zenith/assets/icons/components";
import {
  useNotificationContext,
  useTransactionsContext,
} from "@zenith/context/global-context/hooks";
import CloseCircle from "@zenith/assets/icons/CloseCircle";
import { DASHBOARD, TRANSACTIONS } from "@zenith/routes/paths";
import { downloadFileFromBlob } from "@zenith/utils/Helpers/downloadFileUtil";
import ShareReceiptComponent from "@zenith/features/ShareReceipt/components/share-receipt-component/share-receipt-component";
import { TransactionsParametersExportRecord } from "@zenith/features/Transactions/interfaces";
import { SpinnerComponent, ToastSnackbarComponent } from "@zenith/components";
import CheckCircle from "@zenith/assets/icons/CheckCircle";
import CopyToClipboard from "@zenith/components/ui/CopyToClipboard/CopyToClipboard";
import {
  formatTransactionDate,
  formatTransactionTime,
  shouldShow,
  truncateText,
} from "@zenith/utils/TransactionDisplayHelpers";

/* eslint-disable-next-line */
export interface RefundSuccessfulPageProps {}

const RefundSuccessfulPage = memo(function RefundSuccessfulPage(
  props: RefundSuccessfulPageProps
) {
  const [showEmail, setShowEmail] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { transactionsContext, clearTransactionsContext } =
    useTransactionsContext();

  const {
    amount,
    bankName,
    errorMessage,
    feeValue,
    sessionId,
    status,
    totalAmount,
    transactionDate,
    transactionId,
    refundedTransactionId,
    paymentMethod,
    operationId,
    senderName,
    beneficiaryName,
    beneficiaryAccountNumber,
    description,
  } = transactionsContext;

  const navigate = useNavigate();
  const { setOpen } = useNotificationContext();

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  if (!amount) {
    return <Navigate to={TRANSACTIONS} replace />;
  }

  const mappedPaymentMethod = paymentMethod?.mapPaymentMethod();

  const handleGoToDashboard = () => {
    clearTransactionsContext();
    navigate(DASHBOARD);
  };

  function downloadReceipt() {
    setIsDownloading(true);
    if (operationId) {
      OperationsApiService.exportReceipt(operationId)
        .then((response) => {
          if (response) {
            downloadFileFromBlob(
              response,
              PAYMENTS_RECEIPT_DOWNLOAD_FILENAME + operationId.formatToShowID(),
              TransactionsParametersExportRecord.mediaType.PDF
            );
            notify(
              "Download Successful",
              "Receipt downloaded successfully.",
              "success"
            );
          }
        })
        .catch((error) => {
          throw new Error(error);
          notify(
            "Download Failed",
            "Unable to download receipt. Please try again.",
            "error"
          );
        })
        .finally(() => {
          setIsDownloading(false);
        });
    } else {
      setIsDownloading(false);
      throw new Error("OperationId Undefined");
    }
  }

  function handleShowEmail() {
    setShowEmail(true);
  }

  const getPaymentMethodDisplay = () => {
    return mappedPaymentMethod || "Refund";
  };

  const shouldShowFees = feeValue !== undefined && feeValue !== null && feeValue > 0;
  // Depend on the total, not the fee — otherwise this row disappears on any
  // refund with no fee charged.
  const shouldShowTotal = totalAmount !== undefined && totalAmount !== null;

  return (
    <>
      {toasterData.title && toasterData.message && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}
      <div className="mb-2 mt-12 flex min-h-[calc(100vh-128px)] max-w-[96%] flex-col justify-around">
        <div className="flex w-120 max-w-[98%] flex-col items-center self-center rounded-[20px] border border-neutral-200">
          <div className="flex w-full flex-col self-start p-8">
            {status === OperationDTO.status.SUCCESS ||
            status === OperationDTO.status.PENDING ? (
              <>
                <div className="mt-5 mb-9 flex justify-center">
                <CheckCircle width={80} height={80} />
                </div>
                <h2 className="text-charcoal-black mb-6 text-center text-xl font-medium leading-[28px]">
                  Refund Successful
                </h2>
              </>
            ) : (
              <>
                <div className="mt-2 mb-6 flex justify-center">
                  <CloseCircle transform="scale(1.65)" />
                </div>
                <h2 className="text-charcoal-black mb-6 text-center text-xl font-medium leading-[28px]">
                  Refund Unsuccessful
                </h2>
                <div className="bg-primary-15 mb-12 inline-flex h-14 w-full items-start justify-between rounded-lg py-4 px-1">
                  <div className="flex h-6 items-center justify-start">
                    <CloseCircle transform="scale(0.6)" />
                    <div className="grow text-sm text-zinc-900">
                      {errorMessage}
                    </div>
                  </div>
                </div>
              </>
            )}

            <div className="mb-2 flex justify-between">
              <p className="text-neutral-600">Date</p>
              <p className="font-medium text-neutral-900">
                {formatTransactionDate(transactionDate)}
              </p>
            </div>
            <div className="mb-2 flex justify-between">
              <p className="text-neutral-600">Time</p>
              <p className="font-medium text-neutral-900">
                {formatTransactionTime(transactionDate)}
              </p>
            </div>

            {(status === OperationDTO.status.SUCCESS ||
              status === OperationDTO.status.PENDING) && (
              <>
                {shouldShow(beneficiaryName || senderName) && (
                  <>
                    <hr className="my-4 border-t-1 border-neutral-200" />
                    <div className="mb-2 flex justify-between">
                      <p className="text-neutral-600">Payer's Name</p>
                      <p className="font-medium text-neutral-900">{beneficiaryName || senderName}</p>
                    </div>
                  </>
                )}

                {shouldShow(bankName) && (
                  <>
                    <hr className="my-4 border-t-1 border-neutral-200" />
                    <div className="mb-2 flex justify-between">
                      <p className="text-neutral-600">Payer's Bank</p>
                      <p className="font-medium text-neutral-900">{bankName}</p>
                    </div>
                  </>
                )}

                {/* {shouldShow(beneficiaryAccountNumber) && (
                  <div className="mb-2 flex justify-between">
                    <p className="text-neutral-600">Payer's Account</p>
                    <CopyToClipboard text={beneficiaryAccountNumber} />
                  </div>
                )} */}

                {shouldShow(refundedTransactionId) && (
                  <>
                    <hr className="my-4 border-t-1 border-neutral-200" />
                    <div className="mb-2 flex justify-between">
                      <p className="text-neutral-600">Original Transaction ID</p>
                      <CopyToClipboard text={refundedTransactionId} />
                    </div>
                  </>
                )}

                <hr className="my-4 border-t-1 border-neutral-200" />
                <div className="mb-2 flex justify-between">
                  <p className="text-neutral-600">Transaction Type</p>
                  <p className="font-medium text-neutral-900">{getPaymentMethodDisplay()}</p>
                </div>

                {status === OperationDTO.status.SUCCESS &&
                  shouldShow(transactionId) && (
                    <div className="mb-2 flex justify-between">
                      <p className="text-neutral-600">Transaction ID</p>
                      <CopyToClipboard text={transactionId} />
                    </div>
                  )}

                {shouldShow(sessionId) && (
                  <div className="mb-2 flex justify-between">
                    <p className="text-neutral-600">Session ID</p>
                    <CopyToClipboard text={sessionId} />
                  </div>
                )}

                {shouldShow(description) && (
                  <div className="mb-2 flex justify-between">
                    <p className="text-neutral-600">Description</p>
                    <div className="w-80">
                      <p className="text-right font-medium text-neutral-900 break-words">
                        {truncateText(description)}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            <hr className="my-4 border-t-1 border-neutral-200" />
            <div className="mb-2 flex justify-between">
              <p className="text-neutral-600">Amount Paid</p>
              <p className="font-medium text-neutral-900">{formatCurrency(amount)}</p>
            </div>

            {shouldShowFees && (
              <div className="mb-2 flex justify-between">
                <p className="text-neutral-600">Fees (VAT inclusive)</p>
                <p className="font-medium text-neutral-900">{formatCurrency(feeValue)}</p>
              </div>
            )}

            {shouldShowTotal && (
              <div
                className={`${showEmail ? "mb-6" : "mb-20"} flex justify-between`}
              >
                <strong>
                  <p className="text-base font-semibold text-neutral-600">Total Amount</p>
                </strong>
                <p className="font-medium text-neutral-900">{formatCurrency(totalAmount)}</p>
              </div>
            )}

            {status === OperationDTO.status.SUCCESS ? (
              <>
                {shouldShow(transactionId) && (
                  <div>
                    {showEmail ? (
                      <div className="mb-14">
                        <ShareReceiptComponent
                          notify={notify}
                          operationId={operationId as string}
                          notificationClass="ml-104 mt-16"
                        />
                      </div>
                    ) : (
                      <div className="relative mb-10 flex flex-col items-center">
                        <hr className="w-full border-t-1 border-neutral-200" />
                        <p className="absolute left-[50%] w-[113px] translate-y-[-50%] translate-x-[-50%] bg-white text-center text-sm text-neutral-600">
                          Share receipt
                        </p>
                        <IconButton className="mt-4" onClick={handleShowEmail}>
                          <EmailIcon />
                        </IconButton>
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-6 flex justify-center space-x-4">
                  {shouldShow(transactionId) && (
                    <button
                      id="download-receipt-button"
                      className="h-12 flex-1 rounded-lg border border-neutral-300 bg-white text-base font-normal text-neutral-600"
                      onClick={downloadReceipt}
                      disabled={isDownloading}
                    >
                      {isDownloading ? (
                        <div className="flex items-center justify-center gap-2">
                          <span>Downloading</span>
                          <SpinnerComponent size={20} type="primary" />
                        </div>
                      ) : (
                        "Download Receipt"
                      )}
                    </button>
                  )}

                  <button
                    id="go-to-dashboard-button"
                    type="submit"
                    onClick={handleGoToDashboard}
                    className="h-12 flex-1 rounded-lg bg-primary-300 text-base font-normal text-white"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </>
            ) : (
              <div className="flex justify-center">
                <Button
                  id="send-money-go-to-dashbaord"
                  className={
                    "h-12 w-large self-center rounded-lg bg-primary-300 py-3 px-10 text-base font-medium normal-case text-white hover:bg-primary-300 disabled:bg-zinc-100"
                  }
                  size="large"
                  onClick={handleGoToDashboard}
                >
                  Go to Dashboard
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default RefundSuccessfulPage;